import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Calendar, Clock, User, CheckCircle, XCircle, Video, Phone, ArrowLeft } from 'lucide-react';
import Header from '../common/Header';
import Footer from '../common/Footer';
import chatService from '../../services/chatService';

const ChatConsultationList = () => {
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    fetchConsultations();
  }, []);

  const fetchConsultations = async () => {
    try {
      setLoading(true);
      const response = await chatService.getUserConsultations();
      if (response.success) {
        setConsultations(response.data || response.consultations || []);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching consultations:', err);
      setError(err.error || 'Failed to load consultations');
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: { color: 'bg-green-100 text-green-700', icon: CheckCircle, text: 'Active' },
      completed: { color: 'bg-gray-100 text-gray-700', icon: CheckCircle, text: 'Completed' },
      cancelled: { color: 'bg-red-100 text-red-700', icon: XCircle, text: 'Cancelled' },
    };

    const badge = badges[status] || badges.active;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  // Filter consultations without deduplication - show all chats
  const filteredConsultations = consultations.filter(consultation => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return consultation.status === 'active';
    if (activeTab === 'completed') return consultation.status === 'completed';
    return true;
  }).sort((a, b) => b.id - a.id); // Sort by ID descending (most recent first)

  if (loading) {
    return (
      <>
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50 flex items-center justify-center">
          <div className="text-center">
            <div className="relative">
              <div className="animate-spin rounded-full h-20 w-20 border-4 border-teal-200 border-t-teal-600 mx-auto"></div>
              <MessageCircle className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 text-teal-600 animate-pulse" />
            </div>
            <p className="mt-6 text-gray-700 text-lg font-medium">Loading consultations...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Back Button */}
            <button
              onClick={() => navigate('/online-consultation')}
              className="mb-6 flex items-center gap-2 text-white hover:text-teal-100 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Doctors</span>
            </button>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl md:text-4xl font-bold mb-2">My Consultations</h1>
                <p className="text-teal-100">Chat with your doctors anytime</p>
              </div>
              <button
                onClick={() => navigate('/online-consultation')}
                className="bg-white text-teal-600 px-6 py-3 rounded-xl font-semibold hover:bg-teal-50 transition-colors shadow-lg flex items-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="hidden sm:inline">New Consultation</span>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex gap-2 py-4 overflow-x-auto">
              <button
                onClick={() => setActiveTab('all')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'all'
                    ? 'bg-gradient-to-r from-teal-600 to-blue-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                All ({consultations.length})
              </button>
              <button
                onClick={() => setActiveTab('active')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'active'
                    ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Active ({consultations.filter(c => c.status === 'active').length})
              </button>
              <button
                onClick={() => setActiveTab('completed')}
                className={`px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                  activeTab === 'completed'
                    ? 'bg-gradient-to-r from-gray-600 to-gray-700 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Completed ({consultations.filter(c => c.status === 'completed').length})
              </button>
            </div>
          </div>
        </div>

        {/* Consultations List */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {filteredConsultations.length === 0 ? (
            <div className="text-center py-20">
              <div className="bg-white rounded-2xl shadow-xl p-12 max-w-md mx-auto">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <MessageCircle className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3">No Consultations Yet</h3>
                <p className="text-gray-600 mb-6">
                  Start your first consultation with our expert doctors
                </p>
                <button
                  onClick={() => navigate('/online-consultation')}
                  className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Start Consultation
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredConsultations.map((consultation) => (
                <div
                  key={consultation.id}
                  onClick={() => navigate(`/chat-consultation/${consultation.id}`)}
                  className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden cursor-pointer border-2 border-gray-100 hover:border-teal-200"
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Doctor Avatar */}
                      <div className="relative flex-shrink-0">
                        <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center">
                          <User className="w-8 h-8 text-white" />
                        </div>
                        {consultation.status === 'active' && (
                          <div className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
                        )}
                      </div>

                      {/* Consultation Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">
                              {consultation.doctor_name || 'Doctor'}
                            </h3>
                            <p className="text-sm text-gray-600">
                              Consultation #{consultation.id}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            {getStatusBadge(consultation.status)}
                          </div>
                        </div>

                        {/* Type Badge */}
                        <div className="flex items-center gap-2 mb-3">
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                            <MessageCircle className="w-3 h-3" />
                            {(consultation.consultation_type || consultation.type) === 'chat' ? 'Chat Consultation' : 'Video Consultation'}
                          </span>
                        </div>

                        {/* Consultation Details */}
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            <span>{new Date(consultation.created_at || consultation.start_time).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            <span>{new Date(consultation.created_at || consultation.start_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
                          </div>
                        </div>
                      </div>

                      {/* Action Arrow */}
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center group-hover:bg-teal-200 transition-colors">
                          <MessageCircle className="w-4 h-4 text-teal-600" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-xl font-bold mb-2">Available Hours: 9 AM - 10 PM</h3>
                <p className="text-teal-100">
                  You can send messages anytime, but doctors will reply during available hours
                </p>
              </div>
              <a
                href="tel:+919876543210"
                className="bg-white text-teal-600 px-8 py-4 rounded-xl font-bold hover:bg-teal-50 transition-colors shadow-2xl flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                <span>Emergency: +91 98765 43210</span>
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ChatConsultationList;
